import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["@type", "__typename"];
import { generateHash } from 'crm-index-visualization-core/core/model/hash';
import { datesHash } from '../util/storageMangager';
import { isFeAdded, isInterimView } from 'crm-index-visualization-core/core/utils/vizSettingsAdaptor';
import { extractLegacyDataWellConfig, convertLegacyTableToBackendView, convertLegacyDataWellToBackendView, extractLegacyTableConfig, convertAnyTableSettingsToNewFormat } from './legacy-adapters';
const VIZ_SETTING_STORAGE_PREFIX = 'vizsetting';

/**
 * Represents visualization settings for a specific visualization type.
 * Replaces VizConfig with simplified approach - no versioning, just settings package.
 * Uses different storage keys to avoid JSON parsing conflicts with VizConfig.
 */
export class VizSetting {
  // Private serialization context for backend API calls

  /**
   * Create a new VizSetting
   * @param vizType The name of the visualization type (e.g., 'BOARD', 'table', 'GANTT_CHART')
   * @param config The configuration/settings for the visualization type
   * @param id The ID of the setting
   * @param context Optional context for backend serialization (viewId, objectTypeId)
   */
  constructor(vizType, config, id, context = {}) {
    var _context$viewId, _context$objectTypeId, _context$__typename;
    this.visualizationType = normalizeVizType(vizType);
    this.settings = config;
    this.id = id;

    // Build serialization context with defaults
    this._serializationContext = {
      viewId: (_context$viewId = context.viewId) !== null && _context$viewId !== void 0 ? _context$viewId : -1,
      objectTypeId: (_context$objectTypeId = context.objectTypeId) !== null && _context$objectTypeId !== void 0 ? _context$objectTypeId : '0-999',
      '@type': this._getAtTypeForVizType(this.visualizationType),
      __typename: (_context$__typename = context.__typename) !== null && _context$__typename !== void 0 ? _context$__typename : this._getTypenameForVizType(this.visualizationType)
    };
  }

  /**
   * Maps visualization type to @type field required by backend
   */
  _getAtTypeForVizType(vizType) {
    switch (vizType) {
      case 'TABLE':
        return 'Table Settings';
      case 'BOARD':
        return 'Board Settings';
      case 'GANTT_CHART':
        return 'Gantt Chart Settings';
      case 'REPORT':
        return 'Report Settings';
      case 'MAP':
        return 'Map Settings';
      case 'DATA_WELL':
        return 'DataWell Settings';
      default:
        return `${vizType} Settings`;
    }
  }

  /**
   * Maps visualization type to __typename field required by backend
   * TODO: Maybe this can be removed?
   */
  _getTypenameForVizType(vizType) {
    switch (vizType) {
      case 'TABLE':
        return 'com.hubspot.crm.visualization.settings.models.settings.TableSettings';
      case 'BOARD':
        return 'com.hubspot.crm.visualization.settings.models.settings.BoardSettings';
      case 'GANTT_CHART':
        return 'com.hubspot.crm.visualization.settings.models.settings.GanttChartSettings';
      case 'REPORT':
        return 'com.hubspot.crm.visualization.settings.models.settings.ReportSettings';
      case 'MAP':
        return 'com.hubspot.crm.visualization.settings.models.settings.MapSettings';
      case 'DATA_WELL':
        return 'com.hubspot.crm.visualization.settings.models.settings.DataWellSettings';
      default:
        return `com.hubspot.crm.visualization.settings.models.settings.${vizType}Settings`;
    }
  }

  /**
   * Creates a new VizSetting with updated properties
   * @param updates Partial updates to apply to the config
   * @returns A new VizSetting instance with the updates applied
   */
  with(updates) {
    const updatedConfig = Object.assign({}, this.settings, updates);
    return new VizSetting(this.visualizationType, updatedConfig, this.id, this._serializationContext);
  }

  /**
   * Get the client cache key for this setting
   */
  static getClientCacheKey(hash) {
    return `${VIZ_SETTING_STORAGE_PREFIX}-${hash}`;
  }

  /**
   * Get the client cache key for this instance
   */
  async getClientCacheKey() {
    return VizSetting.getClientCacheKey(await this.hash());
  }

  /**
   * Generate a hash for this setting
   */
  hash() {
    return generateHash(this);
  }

  /**
   * Load a VizSetting from client cache
   * @throws if setting cannot be loaded
   */
  static async fromClientCache(hash, clientStorage) {
    const key = VizSetting.getClientCacheKey(hash);
    const stored = await clientStorage.getItem(key);
    if (!stored) {
      throw new Error(`No viz setting found for hash: ${hash}`);
    }
    const parsed = JSON.parse(stored);
    const {
      visualizationType,
      settings,
      id,
      _serializationContext
    } = parsed;
    return new VizSetting(visualizationType, settings, id, _serializationContext);
  }

  /**
   * Save this setting to client cache
   * @returns hash that can be used to retrieve the setting later
   */
  async toClientCache(clientStorage) {
    const hash = await this.hash();
    const key = await this.getClientCacheKey();
    await Promise.all([clientStorage.setItem(key, JSON.stringify(this)), datesHash(key, Date.now(), clientStorage)]);
    return hash;
  }

  /**
   * Creates a VizSetting instance from a backend view.
   * Extracts visualization settings from the adapted backend view or returns defaults.
   * @param beView - Backend view (potentially adapted with viz settings)
   * @param vizType - The visualization type to extract settings for
   * @throw Error if no viz setting found for view and viz type
   * @returns VizSetting instance with extracted or default configuration
   * @example VizSetting.fromBEView(adaptedView, 'table') // extracts table settings
   */
  static fromBEView(beView, vizType) {
    var _ref;
    if (vizType === 'DATA_WELL') {
      const legacySettings = extractLegacyDataWellConfig(beView);
      return new VizSetting(vizType, legacySettings, -2, {
        viewId: beView.id,
        objectTypeId: beView.objectTypeId
      });
    }
    const backendCurrentViewVisualization = normalizeVizType((_ref = beView.currentViewVisualization) !== null && _ref !== void 0 ? _ref : 'TABLE');
    if (isInterimView(beView) && backendCurrentViewVisualization === vizType) {
      const parsed = JSON.parse(beView.visualizationSettingsString);
      // FIX ME: remove custom code that adds FE-added settings

      if (vizType === 'TABLE' && parsed.id <= 0) {
        const legacySettings = extractLegacyTableConfig(beView);
        const settings = convertAnyTableSettingsToNewFormat(legacySettings);

        // Must be -3
        return new VizSetting(vizType, settings, -3, {
          viewId: beView.id,
          objectTypeId: beView.objectTypeId
        });
      }
      if (isFeAdded(parsed)) {
        throw new Error(`Interim view ${beView.id} has FE-added settings for ${vizType}`);
      }

      // Extract metadata from settings and clean the settings object
      const _parsed$settings = parsed.settings,
        {
          __typename
        } = _parsed$settings,
        cleanSettings = _objectWithoutPropertiesLoose(_parsed$settings, _excluded);
      return new VizSetting(vizType, cleanSettings, parsed.id, {
        viewId: beView.id,
        objectTypeId: beView.objectTypeId,
        __typename: __typename
      });
    }
    throw new Error(`No viz setting found for view: ${beView.id} and viz type: ${vizType}`);
  }

  /**
   * Applies this VizSetting to a backend view with visualization settings.
   * Only updates the settings when visualization types match.
   * For table visualization, implements dual-write to both new and legacy formats.
   * @param beView - Backend view with current visualization settings to update
   * @returns Updated view with new settings if types match, original view if types don't match
   * @throws Never throws - safely handles type mismatches
   * @example vizSetting.toBEView(viewWithMatchingVizType) // Updates settings
   * @example vizSetting.toBEView(viewWithDifferentVizType) // Returns original view
   */
  toBEView(beView) {
    let baseView = 'visualizationSettingsMap' in beView ? beView : Object.assign({}, beView, {
      visualizationSettingsMap: {}
    });
    if (this.isDataWellViz()) {
      baseView = convertLegacyDataWellToBackendView(baseView, this.settings);
      return baseView;
    }
    if (this.isTableViz()) {
      baseView = convertLegacyTableToBackendView(baseView, convertAnyTableSettingsToNewFormat(this.settings));
    }
    baseView.visualizationSettingsMap[this.visualizationType] = this._serializeSettings();
    return baseView;
  }
  _serializeSettings() {
    let settings = this.settings;
    if (this.isTableViz()) {
      settings = convertAnyTableSettingsToNewFormat(this.settings);
    }
    return {
      id: this.id,
      visualizationType: this.visualizationType,
      viewId: this._serializationContext.viewId,
      objectTypeId: this._serializationContext.objectTypeId,
      settings: Object.assign({}, settings, {
        '@type': this._serializationContext['@type'],
        __typename: this._serializationContext.__typename
      })
    };
  }

  /**
   * Returns default VizSetting instance for a visualization type
   * @param vizType - The visualization type ('BOARD', 'table', 'GANTT_CHART', 'REPORT')
   * @returns Default VizSetting instance. Board returns VizSetting with boardProperty, others return empty settings
   * @example VizSetting.default('BOARD') // new VizSetting('BOARD', {boardProperty: 'hs_pipeline_stage'})
   */
  static default(vizType) {
    switch (vizType) {
      case 'BOARD':
        return new VizSetting('BOARD', {
          boardProperty: 'hs_pipeline_stage',
          columnPropertyValues: []
        }, -1, {});
      case 'TABLE':
        return new VizSetting('TABLE', {
          columns: [],
          numberOfFrozenColumns: 0,
          conditionalHighlightRules: []
        }, -1, {});
      case 'GANTT_CHART':
        return new VizSetting('GANTT_CHART', {
          timeUnit: 'DAYS',
          taskStatuses: []
        }, -1, {});
      case 'REPORT':
        return new VizSetting('REPORT', {}, -1, {});
      default:
        return new VizSetting(vizType, {}, -1, {});
    }
  }

  /**Type descriminants */
  isTableViz() {
    return this.visualizationType === 'TABLE';
  }
  isBoardViz() {
    return this.visualizationType === 'BOARD';
  }
  isGanttViz() {
    return this.visualizationType === 'GANTT_CHART';
  }
  isReportingViz() {
    return this.visualizationType === 'REPORT';
  }
  isMapViz() {
    return this.visualizationType === 'MAP';
  }
  isDataWellViz() {
    return this.visualizationType === 'DATA_WELL';
  }
}

/**
 * Maps page type to viz type.
 */
export function normalizeVizType(vizTypeOrPageType) {
  if (vizTypeOrPageType === 'datawell') {
    return 'DATA_WELL';
  }
  if (vizTypeOrPageType === 'DATA_WELL') {
    return 'DATA_WELL';
  }
  return pageTypeToVizType(vizTypeOrPageType);
}
export function pageTypeToVizType(pageType) {
  switch (pageType) {
    case 'list':
      return 'TABLE';
    case 'board':
      return 'BOARD';
    case 'gantt':
      return 'GANTT_CHART';
    case 'report':
      return 'REPORT';
    case 'map':
      return 'MAP';
    default:
      return pageType.toUpperCase();
  }
}

/**
 * Maps viz type to page type.
 */
export function denormalizeVizType(vizTypeOrPageType) {
  switch (vizTypeOrPageType) {
    case 'TABLE':
      return 'list';
    case 'BOARD':
      return 'board';
    case 'GANTT_CHART':
      return 'gantt';
    case 'REPORT':
      return 'report';
    case 'MAP':
      return 'map';
    default:
      return vizTypeOrPageType;
  }
}

/**
 * Maps visualization types to their corresponding setting types
 */